/* eslint-disable react/no-array-index-key */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { ThreeDots } from 'react-loader-spinner';
import '../AdminOrderModal/css/AdminOrderModal.css';
import '../css/AdminPanel.css';
import '../AdminCreateOrderModal/css/AdminCreateOrderModal.css';
import './css/AdminTourActualization.css';
import {
  getTourDetails,
  getTourFlights,
  getOperatorInfo,
} from '../../../Utils/RestUtils';
import { getOrderPayload } from '../../../Utils/OrderUtil';
import {
  getPriceSplit,
  isFlightInsuranceAvailable,
} from '../../../Utils/Constants/PriceUtil';
import {
  isCheapestFlight,
  isFlightValid,
  getCheapestFlightPrice,
  getDefaultFlight,
} from '../../../Utils/FlightUtils';
import {
  FLIGHT_INSURANCE,
  SPLIT_PRICE,
  PROFILE_ORDER_FLIGHT_TO,
  PROFILE_ORDER_FLIGHT_BACK,
  CHOOSE_ANOTHER,
} from '../../../Utils/Constants/GlobalConstants';
import {
  ORDER_TOUR_ID_PLACEHOLDER,
  CREATE_ORDER_FILL_TOUR_ID,
  CREATE_ORDER_TOUR_NOT_FOUND,
  ACTUALIZE,
  RETRY,
  FLIGHT_LOADING,
  FLIGHT_NOT_LOADED,
  ORDER_PREVIEW_FLIGHT,
  ORDER_PREVIEW_TOURISTS,
  ORDER_PREVIEW_TOTAL,
  ORDER_PREVIEW_SECTIONS,
  ORDER_PREVIEW_PRICE,
  ORDER_FIELD_SECTIONS,
  EMPTY_VALUE,
} from '../../../Utils/Constants/AdminConstants';
import OrderTicket from '../../OrderTicket/OrderTicket';
import FlightOption from '../../FlightOption/FlightOption';
import { ReactComponent as DefaultCheck } from '../../../images/default-check-r.svg';
import { ReactComponent as ActiveCheck } from '../../../images/active-check-r.svg';

const fieldLabels = ORDER_FIELD_SECTIONS.reduce(
  (result, section) =>
    section.fields.reduce(
      (labels, field) => ({ ...labels, [field.key]: field.label }),
      result,
    ),
  { touristsText: ORDER_PREVIEW_TOURISTS, totalPrice: ORDER_PREVIEW_TOTAL },
);

const getDefaultSplit = (split) =>
  (split.includes(100) ? 100 : split[split.length - 1]);

// tour id input, actualization and the preview of the order built from it;
// the built order (without contacts) goes to handleOrderChange, null if none
function AdminTourActualization(props) {
  const { isVisible, handleOrderChange, withoutFlightText } = props;
  const [tourId, setTourId] = useState('');
  const [tour, setTour] = useState(null);
  const [isTourLoading, setIsTourLoading] = useState(false);
  const [flight, setFlight] = useState(null);
  const [isFlightLoading, setIsFlightLoading] = useState(false);
  const [isFlightsListVisible, setIsFlightsListVisible] = useState(false);
  const [insuranceChecked, setInsuranceChecked] = useState(true);
  const [splitPrice, setSplitPrice] = useState(100);
  const [message, setMessage] = useState('');
  // drops responses of an outdated actualization
  const actualizeCounter = useRef(0);

  const resetTour = () => {
    actualizeCounter.current += 1;
    setTour(null);
    setIsTourLoading(false);
    setFlight(null);
    setIsFlightLoading(false);
    setIsFlightsListVisible(false);
    setMessage('');
  };

  // start with an empty form on every open
  useEffect(() => {
    if (isVisible) {
      resetTour();
      setTourId('');
    }
  }, [isVisible]);

  const isInsuranceAvailable = () =>
    isFlightInsuranceAvailable(tour.acualizationData, tour.operatorInfo);

  const buildOrder = () => {
    const { acualizationData } = tour;
    const selectedFlight = flight ? flight.selectedFlight : {};

    return getOrderPayload({
      acualizationData,
      selectedFlight,
      tourInfo: flight ? flight.tourInfo : {},
      insuranceChecked: insuranceChecked && isInsuranceAvailable(),
      promoCode: {},
      isPromoApplied: false,
      splitPrice,
      priceCurrency: flight
        ? selectedFlight.price.currency
        : acualizationData.currency,
      tourId: tour.tourId,
      phoneNumber: '',
      email: '',
      note: '',
    });
  };

  useEffect(() => {
    handleOrderChange(tour ? buildOrder() : null);
  }, [tour, flight, insuranceChecked, splitPrice]);

  const handleTourIdChange = (e) => {
    // another tour id needs a new actualization
    resetTour();
    setTourId(e.target.value);
  };

  const fetchFlight = (id, local) => {
    setFlight(null);
    setIsFlightLoading(true);
    setIsFlightsListVisible(false);

    getTourFlights(id)
      .then((response) => {
        if (local !== actualizeCounter.current) {
          return;
        }

        const data = response ? response.data : null;
        const flights =
          data && !data.iserror && data.flights
            ? data.flights.filter(isFlightValid)
            : [];

        if (flights.length > 0) {
          setFlight({
            selectedFlight: getDefaultFlight(flights),
            tourInfo: data.tourinfo || {},
            flights,
          });
        }
      })
      .catch(() => {})
      .finally(() => {
        if (local === actualizeCounter.current) {
          setIsFlightLoading(false);
        }
      });
  };

  const fetchTour = async (id) => {
    const response = await getTourDetails(id);
    const data = response && response.data ? response.data.data : null;

    if (!data || data.success === 0 || !data.tour) {
      return null;
    }

    const acualizationData = data.tour;
    const operatorResponse = await getOperatorInfo(
      acualizationData.operatorcode,
    ).catch(() => null);
    const operatorInfo =
      operatorResponse && operatorResponse.data ? operatorResponse.data : {};

    return { acualizationData, operatorInfo };
  };

  const handleActualize = () => {
    const id = tourId.trim();

    if (id.length === 0) {
      setMessage(CREATE_ORDER_FILL_TOUR_ID);
      return;
    }

    resetTour();
    const local = actualizeCounter.current;

    setIsTourLoading(true);
    // flights are slow, so they load on their own and never block the tour
    fetchFlight(id, local);

    fetchTour(id)
      .then((result) => {
        if (local !== actualizeCounter.current) {
          return;
        }

        if (!result) {
          setMessage(CREATE_ORDER_TOUR_NOT_FOUND);
          return;
        }

        const split = getPriceSplit(
          result.acualizationData,
          result.operatorInfo,
        );

        setInsuranceChecked(true);
        setSplitPrice(getDefaultSplit(split));
        setTour({ ...result, tourId: id, split });
      })
      .catch(() => {
        if (local === actualizeCounter.current) {
          setMessage(CREATE_ORDER_TOUR_NOT_FOUND);
        }
      })
      .finally(() => {
        if (local === actualizeCounter.current) {
          setIsTourLoading(false);
        }
      });
  };

  const getValueText = (value) => {
    if (Array.isArray(value)) {
      return value.length > 0 ? value.join('\n') : EMPTY_VALUE;
    }

    return value || value === 0 ? value : EMPTY_VALUE;
  };

  const getSplitClass = (item) =>
    `ap_small_btn bm_text_14 ${
      splitPrice === item ? 'btn__yellow' : 'light_gray_button'
    }`;

  const renderSpinner = () => (
    <ThreeDots
      visible
      height="24"
      width="30"
      color="rgba(0, 0, 0, .5)"
      radius="5"
      ariaLabel="three-dots-loading"
    />
  );

  const renderRow = (order, key) => (
    <div key={`acom-row-${key}`} className="aom_row">
      <div className="aom_label gr_text_14">{fieldLabels[key]}</div>
      <div className="aom_value acom_value br_text_16">
        {getValueText(order[key])}
      </div>
    </div>
  );

  const renderSection = (order, section) => (
    <div key={`acom-section-${section.title}`} className="aom_section">
      <div className="bm_text_16 mb_12">{section.title}</div>
      <div className="aom_rows">
        {section.keys.map((key) => renderRow(order, key))}
      </div>
    </div>
  );

  const renderFlightStatus = () => {
    if (isFlightLoading) {
      return (
        <div className="acom_flight_status gr_text_14">
          {renderSpinner()}
          {FLIGHT_LOADING}
        </div>
      );
    }

    return (
      <div className="acom_flight_status">
        <span className="ap_message_error br_text_14">{FLIGHT_NOT_LOADED}</span>
        <button
          type="button"
          className="ap_small_btn light_gray_button bm_text_14"
          onClick={() => fetchFlight(tour.tourId, actualizeCounter.current)}
        >
          {RETRY}
        </button>
      </div>
    );
  };

  const selectFlight = (selectedFlight) => {
    setFlight({ ...flight, selectedFlight });
    setIsFlightsListVisible(false);
  };

  const renderFlightsList = () => {
    const { flights } = flight;
    const cheapestPrice = getCheapestFlightPrice(flights);

    return (
      <div className="ato_flights custom_scroll_container">
        {flights.map((item, index) => (
          <FlightOption
            key={`ato-flight-${index}`}
            flight={item}
            cheapestPrice={cheapestPrice}
            isSelected={item === flight.selectedFlight}
            cheapest={isCheapestFlight(item, flights)}
            handleSelect={selectFlight}
          />
        ))}
      </div>
    );
  };

  const renderFlightChoice = () => (
    <>
      <button
        type="button"
        className="ap_small_btn light_gray_button bm_text_14 ato_choose_flight"
        onClick={() => setIsFlightsListVisible(!isFlightsListVisible)}
      >
        {`${CHOOSE_ANOTHER} (${flight.flights.length})`}
      </button>
      {isFlightsListVisible && renderFlightsList()}
    </>
  );

  const renderFlight = (order) => (
    <div className="aom_section">
      <div className="bm_text_16 mb_12">{ORDER_PREVIEW_FLIGHT}</div>
      {flight ? (
        <>
          <div className="aom_tickets">
            {order.toTrip.length > 0 && (
              <OrderTicket
                title={PROFILE_ORDER_FLIGHT_TO}
                segments={order.toTrip}
              />
            )}
            {order.backTrip.length > 0 && (
              <OrderTicket
                title={PROFILE_ORDER_FLIGHT_BACK}
                segments={order.backTrip}
              />
            )}
          </div>
          {flight.flights.length > 1 && renderFlightChoice()}
        </>
      ) : (
        renderFlightStatus()
      )}
    </div>
  );

  const renderOptions = () => (
    <div className="aom_section">
      {isInsuranceAvailable() && (
        <button
          type="button"
          className="acom_check br_text_14"
          onClick={() => setInsuranceChecked(!insuranceChecked)}
        >
          {insuranceChecked ? <ActiveCheck /> : <DefaultCheck />}
          {FLIGHT_INSURANCE}
        </button>
      )}
      {tour.split.length > 1 && (
        <div className="acom_split">
          <div className="gr_text_14">{SPLIT_PRICE}</div>
          <div className="acom_split_buttons">
            {tour.split.map((item) => (
              <button
                key={`acom-split-${item}`}
                type="button"
                className={getSplitClass(item)}
                onClick={() => setSplitPrice(item)}
              >
                {`${item}%`}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const renderPreview = () => {
    const order = buildOrder();
    const hasOptions = isInsuranceAvailable() || tour.split.length > 1;

    return (
      <div className="acom_preview">
        {ORDER_PREVIEW_SECTIONS.map((section) => renderSection(order, section))}
        {renderFlight(order)}
        {renderSection(order, ORDER_PREVIEW_PRICE)}
        {hasOptions && renderOptions()}
      </div>
    );
  };

  return (
    <>
      <div className="acom_line">
        <input
          className="ap_text_input acom_input"
          type="text"
          value={tourId}
          placeholder={ORDER_TOUR_ID_PLACEHOLDER}
          onChange={handleTourIdChange}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleActualize();
            }
          }}
        />
        <button
          type="button"
          className="ap_btn light_gray_button dbm_text_16"
          disabled={isTourLoading}
          onClick={handleActualize}
        >
          {isTourLoading ? renderSpinner() : ACTUALIZE}
        </button>
      </div>

      {message.length > 0 && (
        <div className="ap_message ap_message_error br_text_14">{message}</div>
      )}

      {isVisible && tour && renderPreview()}

      {tour && !flight && (
        <div className="gr_text_14">{withoutFlightText}</div>
      )}
    </>
  );
}

AdminTourActualization.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  handleOrderChange: PropTypes.func.isRequired,
  withoutFlightText: PropTypes.string.isRequired,
};

export default AdminTourActualization;
